"use client";

import { useState } from "react";
import Button, { ButtonVariant } from "@/app/(components)/generics/Button/Button";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import { SelectInput } from "@/app/(components)/generics/inputs/default/SelectInput/SelectInput";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { createPatient } from "@/app/actions/patient/action";
import { ModalStore } from "@/app/stores/ModalStore";
import { UserStore } from "@/app/stores/UserStore";
import { TKey } from "@/app/translations/TKey";
import { T, TErrors } from "@/app/translations/Translations";
import styles from "./AddPatient.module.css";

type Props = { lang: string; onSuccess?: () => void };

export default function AddPatient({ lang, onSuccess }: Props) {
  const { closeModal } = ModalStore();
  const user = UserStore((state) => state.user);

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    birthDate: "",
    gender: "",
    shoeSize: 0,
    footSize: "",
    weight: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    setLoading(true);

    const result = await createPatient(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        birthDate: data.birthDate || undefined,
        gender: data.gender || null,
        shoeSize: data.shoeSize || undefined,
        footSize: data.footSize || undefined,
        weight: data.weight || undefined,
        notes: data.notes || undefined,
      },
      lang,
    );

    if (result.success) {
      onSuccess?.();
      closeModal();
    } else if (result.fieldErrors) {
      setFieldErrors(TErrors(result.fieldErrors, lang));
    } else {
      setError(T(TKey.ERROR, lang) as string);
    }

    setLoading(false);
  };

  if (!user?.practitionerProfile) {
    return (
      <div className={styles.noPractitionerContainer}>
        <p className={styles.noPractitionerTitle}>{T(TKey.PATIENT_NO_PRACTITIONER_TITLE, lang)}</p>
        <p className={styles.noPractitionerText}>{T(TKey.PATIENT_NO_PRACTITIONER_TEXT, lang)}</p>
        <div className={styles.actions}>
          <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
            {T(TKey.CANCEL, lang)}
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => ModalStore.getState().openSettings(lang, "practitioner")}
          >
            {T(TKey.PATIENT_NO_PRACTITIONER_ACTION, lang)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className={styles.row}>
        <Input
          data={data}
          dataKey="firstName"
          label={T(TKey.FIRST_NAME, lang) as string}
          lang={lang}
          required
          showRequiredLabel
          placeholder={T(TKey.FIRST_NAME, lang) as string}
          onChange={(d) => setData({ ...d })}
          errorMessage={fieldErrors.firstName}
        />
        <Input
          data={data}
          dataKey="lastName"
          label={T(TKey.LAST_NAME, lang) as string}
          lang={lang}
          required
          showRequiredLabel
          placeholder={T(TKey.LAST_NAME, lang) as string}
          onChange={(d) => setData({ ...d })}
          errorMessage={fieldErrors.lastName}
        />
      </div>

      <div className={styles.row}>
        <Input
          data={data}
          dataKey="birthDate"
          type={TextInputType.DATE}
          label={T(TKey.PATIENT_BIRTH_DATE, lang) as string}
          lang={lang}
          showRequiredLabel
          onChange={(d) => setData({ ...d })}
        />
        <SelectInput
          data={data}
          dataKey="gender"
          label={T(TKey.PATIENT_GENDER, lang) as string}
          lang={lang}
          showRequiredLabel
          withEmptyOption
          options={[
            ["MALE", T(TKey.PATIENT_GENDER_MALE, lang) as string],
            ["FEMALE", T(TKey.PATIENT_GENDER_FEMALE, lang) as string],
            ["OTHER", T(TKey.PATIENT_GENDER_OTHER, lang) as string],
          ]}
          onChange={(d) => setData({ ...d })}
        />
      </div>

      <div className={styles.row}>
        <Input
          data={data}
          dataKey="shoeSize"
          type={TextInputType.NUMBER}
          label={T(TKey.PATIENT_SIZE_EU, lang) as string}
          lang={lang}
          showRequiredLabel
          placeholder=""
          hideZero
          onChange={(d) => setData({ ...d })}
        />
        <Input
          data={data}
          dataKey="footSize"
          label={T(TKey.PATIENT_SIZE_MM, lang) as string}
          lang={lang}
          showRequiredLabel
          placeholder=""
          onChange={(d) => setData({ ...d })}
        />
      </div>

      <Input
        data={data}
        dataKey="weight"
        label={T(TKey.PATIENT_WEIGHT, lang) as string}
        lang={lang}
        showRequiredLabel
        placeholder=""
        onChange={(d) => setData({ ...d })}
      />

      <Input
        data={data}
        dataKey="notes"
        type={TextInputType.TEXTAREA}
        label={T(TKey.PATIENT_NOTES, lang) as string}
        lang={lang}
        showRequiredLabel
        placeholder=""
        onChange={(d) => setData({ ...d })}
      />

      {error && <ErrorMessage message={error} />}

      <div className={styles.actions}>
        <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
          {T(TKey.CANCEL, lang)}
        </Button>
        <Button variant={ButtonVariant.PRIMARY} type="submit" loading={loading}>
          {T(TKey.ADD_PATIENT, lang)}
        </Button>
      </div>
    </form>
  );
}
